import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Filter, Loader2, RefreshCw, RotateCcw, X } from 'lucide-react';
import { DataTable, type Column } from '@/components/shared/DataTable';
import { useCurrency } from '@/hooks/useCurrency';
import type {
  ForecastReviewAlert,
  ForecastReviewAlertsComparisonContext,
  ForecastReviewAlertsImpactSummary,
} from '@/services/forecastReviewService';
import {
  getForecastAlertBusinessMessage,
  getForecastAlertTypeLabel,
} from '../utils/forecastAlertDisplay';
import { SeverityBadge } from './ForecastReviewTableCells';

export type AlertInboxStatusFilter = 'open' | 'resolved';

export interface AlertsInboxPagination {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
}

interface AlertsInboxProps {
  alerts: ForecastReviewAlert[];
  loading?: boolean;
  level: 'product' | 'family' | 'brand' | 'supplier';
  viewType: 'units' | 'value';
  statusFilter: AlertInboxStatusFilter;
  onStatusFilterChange: (status: AlertInboxStatusFilter) => void;
  comparisonContext?: ForecastReviewAlertsComparisonContext | null;
  impactSummary?: ForecastReviewAlertsImpactSummary | null;
  pagination: AlertsInboxPagination;
  onLevelChange: (level: 'product' | 'family' | 'brand' | 'supplier') => void;
  onApprove: (alert: ForecastReviewAlert) => void;
  onReject: (alert: ForecastReviewAlert) => void;
  onReactivate: (alert: ForecastReviewAlert) => void;
  /** Filtra gráfico y KPIs (embudo). */
  onApplyChartFilter: (alert: ForecastReviewAlert) => void;
  /** Clic en fila agregada → desglose a producto. */
  onDrillDown?: (alert: ForecastReviewAlert) => void;
  actionAlertId?: number | null;
  onBulkApprove?: () => void;
  onBulkReject?: () => void;
  onRefreshSupply?: () => void;
  bulkActionsRunning?: boolean;
  refreshSupplyRunning?: boolean;
}

function canReactivateAlert(item: ForecastReviewAlert): boolean {
  return (
    item.alert_type === 'probable_eol' &&
    item.status === 'resolved' &&
    item.resolution_outcome === 'approved'
  );
}

function statusLabel(status: string, t: (key: string) => string): string {
  const key = `forecastReview.alerts.statusValues.${status}`;
  const translated = t(key);
  return translated !== key ? translated : status;
}

function formatVariation(v: number | null | undefined): string {
  if (v == null) return '—';
  const sign = v > 0 ? '+' : '';
  return `${sign}${(v * 100).toFixed(1)}%`;
}

function variationClass(v: number | null | undefined): string {
  if (v == null) return 'text-gray-500';
  if (Math.abs(v) >= 0.2) return 'font-medium text-red-700';
  if (Math.abs(v) >= 0.1) return 'font-medium text-amber-700';
  return 'text-gray-700';
}

function abcBadgeClass(abc: string): string {
  if (abc === 'A') return 'bg-emerald-100 text-emerald-800';
  if (abc === 'B') return 'bg-amber-100 text-amber-800';
  if (abc === 'C') return 'bg-gray-100 text-gray-700';
  return 'bg-gray-100 text-gray-600';
}

function xyzBadgeClass(xyz: string): string {
  if (xyz === 'X') return 'bg-blue-100 text-blue-800';
  if (xyz === 'Y') return 'bg-violet-100 text-violet-800';
  if (xyz === 'Z') return 'bg-orange-100 text-orange-800';
  return 'bg-gray-100 text-gray-600';
}

function ClassificationBadges({ classification }: { classification?: ForecastReviewAlert['classification'] }) {
  const { t } = useTranslation();
  const abc = classification?.abc_quantity;
  const xyz = classification?.xyz_class;
  if (!abc && !xyz) return <span className="text-gray-400">—</span>;
  return (
    <div className="flex items-center gap-1">
      {abc ? (
        <span
          className={`inline-flex rounded px-1.5 py-0.5 text-xs font-semibold ${abcBadgeClass(abc)}`}
          title={
            classification?.abc_value
              ? t('forecastReview.comparison.abcValueTooltip', { value: classification.abc_value })
              : undefined
          }
        >
          {abc}
        </span>
      ) : null}
      {abc && xyz ? <span className="text-xs text-gray-400">·</span> : null}
      {xyz ? (
        <span className={`inline-flex rounded px-1.5 py-0.5 text-xs font-semibold ${xyzBadgeClass(xyz)}`}>
          {xyz}
        </span>
      ) : null}
    </div>
  );
}

function impactDirectionKey(pct: number | null | undefined): 'inflate' | 'reduce' | 'neutral' {
  if (pct == null || Math.abs(pct) < 0.005) return 'neutral';
  return pct > 0 ? 'inflate' : 'reduce';
}

function AlertsImpactBanner({
  summary,
  viewType,
  onBulkApprove,
  onBulkReject,
  onRefreshSupply,
  bulkActionsRunning,
  refreshSupplyRunning,
}: {
  summary: ForecastReviewAlertsImpactSummary;
  viewType: 'units' | 'value';
  onBulkApprove?: () => void;
  onBulkReject?: () => void;
  onRefreshSupply?: () => void;
  bulkActionsRunning?: boolean;
  refreshSupplyRunning?: boolean;
}) {
  const { t } = useTranslation();
  const { formatViewAmount } = useCurrency();
  const formatAmount = (value: number | null | undefined) =>
    value == null ? '—' : formatViewAmount(value, viewType);
  const sharePct = summary.share_of_forecast_pct;
  const impactPct = summary.impact_on_forecast_pct;
  const direction = impactDirectionKey(impactPct);
  const hasOpenAlerts = summary.alert_count > 0;
  const actionsDisabled = Boolean(bulkActionsRunning || refreshSupplyRunning);

  return (
    <div className="rounded-lg border border-gray-200 bg-gradient-to-r from-slate-50 to-white px-4 py-3 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-gray-900">{t('forecastReview.alerts.impactTitle')}</p>
          <p className="mt-0.5 text-xs text-gray-500">
            {t('forecastReview.alerts.impactScope', {
              alerts: summary.alert_count,
              products: summary.alerted_product_count,
            })}
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {onBulkApprove ? (
            <button
              type="button"
              onClick={onBulkApprove}
              disabled={actionsDisabled || !hasOpenAlerts}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-emerald-300 bg-white px-3 py-1.5 text-xs font-medium text-emerald-800 hover:bg-emerald-50 disabled:opacity-50"
            >
              <Check className="h-3.5 w-3.5" />
              {t('forecastReview.alerts.approveAll')}
            </button>
          ) : null}
          {onBulkReject ? (
            <button
              type="button"
              onClick={onBulkReject}
              disabled={actionsDisabled || !hasOpenAlerts}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              <X className="h-3.5 w-3.5" />
              {t('forecastReview.alerts.rejectAll')}
            </button>
          ) : null}
          {onRefreshSupply ? (
            <button
              type="button"
              onClick={onRefreshSupply}
              disabled={actionsDisabled}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-stocker-primary/30 bg-white px-3 py-1.5 text-xs font-medium text-stocker-primary hover:bg-stocker-primary/5 disabled:opacity-50"
            >
              {refreshSupplyRunning ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              {t('forecastReview.alerts.refreshSupply')}
            </button>
          ) : null}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-4 text-sm">
          <div>
            <div className="text-xs text-gray-500">{t('forecastReview.alerts.impactForecastInAlerts')}</div>
            <div className="font-semibold text-gray-900">
              {formatAmount(summary.alerts_forecast_total)}
              {sharePct != null ? (
                <span className="ml-1 text-xs font-normal text-gray-500">
                  ({t('forecastReview.alerts.impactShareOfTotal', { pct: (sharePct * 100).toFixed(1) })})
                </span>
              ) : null}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">{t('forecastReview.alerts.impactVsRecent')}</div>
            <div className={`font-semibold ${variationClass(summary.variation_vs_recent)}`}>
              {formatVariation(summary.variation_vs_recent)}
              <span className="ml-1 text-xs font-normal text-gray-500">
                ({formatAmount(summary.delta_vs_recent)})
              </span>
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">{t('forecastReview.alerts.impactOnTotalForecast')}</div>
            <div className={`font-semibold ${variationClass(impactPct)}`}>
              {impactPct != null ? formatVariation(impactPct) : '—'}
              {impactPct != null ? (
                <span className="ml-1 text-xs font-normal text-gray-600">
                  ({t(`forecastReview.alerts.impactDirection.${direction}`)})
                </span>
              ) : null}
            </div>
          </div>
      </div>
    </div>
  );
}

export function AlertsInbox({
  alerts,
  loading,
  level,
  viewType,
  statusFilter,
  onStatusFilterChange,
  comparisonContext,
  impactSummary,
  pagination,
  onLevelChange,
  onApprove,
  onReject,
  onReactivate,
  onApplyChartFilter,
  onDrillDown,
  actionAlertId,
  onBulkApprove,
  onBulkReject,
  onRefreshSupply,
  bulkActionsRunning,
  refreshSupplyRunning,
}: AlertsInboxProps) {
  const { t } = useTranslation();
  const { formatViewAmount } = useCurrency();
  const formatAmount = (value: number | null | undefined) =>
    value == null ? '—' : formatViewAmount(value, viewType);
  const isProductLevel = level === 'product';
  const isAggregatedLevel = !isProductLevel;
  const periodUnit = comparisonContext?.period_unit ?? 'periodo';
  const showMetricsHint = comparisonContext != null;

  const levels: Array<{ id: typeof level; label: string }> = [
    { id: 'product', label: t('forecastReview.alerts.levelProduct') },
    { id: 'family', label: t('forecastReview.alerts.levelFamily') },
    { id: 'brand', label: t('forecastReview.alerts.levelBrand') },
    { id: 'supplier', label: t('forecastReview.alerts.levelSupplier') },
  ];

  const metricColumns = useMemo<Column[]>(
    () => [
      {
        header: t('forecastReview.alerts.avgRecent'),
        accessor: 'metrics_recent',
        align: 'right' as const,
        numeric: true,
        cell: (_: unknown, item: ForecastReviewAlert) =>
          formatAmount(item.metrics?.avg_recent_sales),
      },
      {
        header: t('forecastReview.alerts.avgLastYear'),
        accessor: 'metrics_ly',
        align: 'right' as const,
        numeric: true,
        cell: (_: unknown, item: ForecastReviewAlert) =>
          formatAmount(item.metrics?.avg_last_year_sales),
      },
      {
        header: t('forecastReview.alerts.avgForecast'),
        accessor: 'metrics_forecast',
        align: 'right' as const,
        numeric: true,
        cell: (_: unknown, item: ForecastReviewAlert) => (
          <div>
            <div className="font-medium text-gray-900">
              {formatAmount(item.metrics?.avg_forecast)}
            </div>
            {item.metrics?.variation_vs_recent != null ? (
              <div className={`text-xs ${variationClass(item.metrics.variation_vs_recent)}`}>
                {t('forecastReview.alerts.vsRecent')}: {formatVariation(item.metrics.variation_vs_recent)}
              </div>
            ) : null}
          </div>
        ),
      },
    ],
    [t, viewType]
  );

  const columns = useMemo((): Column[] => {
    const base: Column[] = [
      {
        header: t('forecastReview.comparison.severity'),
        accessor: 'severity',
        cell: (value: string) => <SeverityBadge severity={value} />,
      },
      {
        header: t('forecastReview.alerts.type'),
        accessor: 'alert_type_label',
        cell: (_: string, item: ForecastReviewAlert) => (
          <span className="text-gray-700">{getForecastAlertTypeLabel(item, t)}</span>
        ),
      },
    ];

    if (isProductLevel) {
      base.push(
        {
          header: t('forecastReview.alerts.product'),
          accessor: 'element_name',
          cell: (_: string, item: ForecastReviewAlert) => (
            <div className="min-w-[8rem]">
              <div className="font-medium text-gray-900">{item.element_name ?? '—'}</div>
              {item.element_code || item.family_name ? (
                <div className="text-xs text-gray-500">
                  {item.element_code ? <span>{item.element_code}</span> : null}
                  {item.element_code && item.family_name ? (
                    <span className="text-gray-400"> · </span>
                  ) : null}
                  {item.family_name ? <span className="text-gray-600">{item.family_name}</span> : null}
                </div>
              ) : null}
            </div>
          ),
        },
        {
          header: t('forecastReview.alerts.supplier'),
          accessor: 'supplier_name',
          cell: (value: string | null | undefined) => <span>{value ?? '—'}</span>,
        },
        {
          header: t('forecastReview.alerts.abcXyz'),
          accessor: 'classification',
          align: 'center' as const,
          cell: (_: unknown, item: ForecastReviewAlert) => (
            <ClassificationBadges classification={item.classification} />
          ),
        },
        ...metricColumns
      );
    } else {
      const elementHeader =
        level === 'family'
          ? t('forecastReview.alerts.family')
          : level === 'brand'
            ? t('forecastReview.alerts.levelBrand')
            : t('forecastReview.alerts.supplier');

      base.push(
        {
          header: elementHeader,
          accessor: 'element_name',
          cell: (_: string, item: ForecastReviewAlert) => (
            <div className="min-w-[8rem]">
              <div className="font-medium text-[#1681ba] hover:underline">{item.element_name ?? '—'}</div>
              {item.alert_count && item.alert_count > 1 ? (
                <div className="text-xs text-gray-500">
                  {t('forecastReview.alerts.alertCount', { count: item.alert_count })}
                </div>
              ) : null}
              {onDrillDown ? (
                <div className="text-xs text-gray-400">{t('forecastReview.alerts.drillDownHint')}</div>
              ) : null}
            </div>
          ),
        },
        ...metricColumns
      );
    }

    base.push(
      {
        header: t('forecastReview.alerts.message'),
        accessor: 'message',
        cell: (_: string | null, item: ForecastReviewAlert) => {
          const text = getForecastAlertBusinessMessage(item, t);
          return (
            <span className="block max-w-[18rem] text-sm leading-snug text-gray-700" title={text}>
              {text}
            </span>
          );
        },
      },
      {
        header: t('forecastReview.alerts.status'),
        accessor: 'status',
        cell: (value: string) => <span className="text-gray-600">{statusLabel(value, t)}</span>,
      },
      {
        header: t('forecastReview.alerts.actions'),
        accessor: 'id',
        align: 'right' as const,
        cell: (_: number, item: ForecastReviewAlert) => (
          <div className="flex justify-end gap-1">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onApplyChartFilter(item);
              }}
              className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
              title={t('forecastReview.alerts.viewInChart')}
            >
              <Filter className="h-3 w-3" />
            </button>
            {item.status === 'open' && level === 'product' ? (
              <>
                <button
                  type="button"
                  disabled={actionAlertId === item.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    onApprove(item);
                  }}
                  className="inline-flex items-center gap-1 rounded border border-emerald-300 bg-white px-2 py-1 text-xs text-emerald-800 hover:bg-emerald-50 disabled:opacity-50"
                  title={t('forecastReview.alerts.approve')}
                >
                  <Check className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  disabled={actionAlertId === item.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    onReject(item);
                  }}
                  className="inline-flex items-center gap-1 rounded border border-red-300 bg-white px-2 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50"
                  title={t('forecastReview.alerts.reject')}
                >
                  <X className="h-3 w-3" />
                </button>
              </>
            ) : null}
            {canReactivateAlert(item) && level === 'product' ? (
              <button
                type="button"
                disabled={actionAlertId === item.id}
                onClick={(e) => {
                  e.stopPropagation();
                  onReactivate(item);
                }}
                className="inline-flex items-center gap-1 rounded border border-sky-300 bg-white px-2 py-1 text-xs text-sky-800 hover:bg-sky-50 disabled:opacity-50"
                title={t('forecastReview.alerts.reactivate')}
              >
                <RotateCcw className="h-3 w-3" />
              </button>
            ) : null}
          </div>
        ),
      }
    );

    return base;
  }, [
    isProductLevel,
    level,
    metricColumns,
    t,
    onApplyChartFilter,
    onApprove,
    onReject,
    onReactivate,
    onDrillDown,
    actionAlertId,
  ]);

  const tableData = useMemo(
    () => alerts.map((alert) => ({ ...alert, _rowKey: `${alert.level}-${alert.element_id}-${alert.id}` })),
    [alerts]
  );

  const handleRowClick = isAggregatedLevel && onDrillDown
    ? (item: ForecastReviewAlert) => onDrillDown(item)
    : undefined;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-medium text-gray-900">{t('forecastReview.alerts.title')}</h3>
          {showMetricsHint ? (
            <p className="mt-0.5 text-xs text-gray-500">
              {t('forecastReview.alerts.metricsHint', {
                count: comparisonContext!.horizon_periods,
                unit: periodUnit,
                unitLabel:
                  comparisonContext!.horizon_periods === 1
                    ? periodUnit
                    : periodUnit === 'semana'
                      ? t('forecastReview.kpis.periodUnitWeekPlural')
                      : t('forecastReview.kpis.periodUnitMonthPlural'),
                forecast: comparisonContext!.forecast_period_label,
                recent: comparisonContext!.recent_period_label,
                lastYear: comparisonContext!.last_year_period_label,
              })}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-md border border-gray-200 bg-white p-0.5 text-xs shadow-sm">
            {(
              [
                { id: 'open' as const, label: t('forecastReview.alerts.statusFilterOpen') },
                { id: 'resolved' as const, label: t('forecastReview.alerts.statusFilterResolved') },
              ] as const
            ).map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => onStatusFilterChange(opt.id)}
                className={`rounded px-2.5 py-1 ${statusFilter === opt.id ? 'bg-gray-100 font-medium text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-md border border-gray-200 bg-white p-0.5 text-xs shadow-sm">
            {levels.map((l) => (
              <button
                key={l.id}
                type="button"
                onClick={() => onLevelChange(l.id)}
                className={`rounded px-2.5 py-1 ${level === l.id ? 'bg-gray-100 font-medium text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {statusFilter === 'open' && impactSummary && impactSummary.alert_count > 0 ? (
        <AlertsImpactBanner
          summary={impactSummary}
          viewType={viewType}
          onBulkApprove={onBulkApprove}
          onBulkReject={onBulkReject}
          onRefreshSupply={onRefreshSupply}
          bulkActionsRunning={bulkActionsRunning}
          refreshSupplyRunning={refreshSupplyRunning}
        />
      ) : null}

      {loading && alerts.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-12 text-center text-sm text-gray-500 shadow-sm">
          {t('forecastReview.alerts.loading')}
        </div>
      ) : !loading && alerts.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-12 text-center text-sm text-gray-500 shadow-sm">
          {statusFilter === 'resolved'
            ? t('forecastReview.alerts.emptyResolved')
            : t('forecastReview.alerts.empty')}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={tableData}
          compact
          loading={loading}
          pagination
          onRowClick={handleRowClick}
          rowClassName={handleRowClick ? () => 'cursor-pointer' : undefined}
          externalPagination={{
            currentPage: pagination.currentPage,
            totalPages: pagination.totalPages,
            totalCount: pagination.totalCount,
            pageSize: pagination.pageSize,
            onPageChange: pagination.onPageChange,
            onPageSizeChange: pagination.onPageSizeChange,
          }}
        />
      )}
    </div>
  );
}
